import { makeAutoObservable, runInAction } from 'mobx';
import { t } from '@/shared/i18n';
import { EtaContext, EtaStage, IEtaLoader } from '@/sharedTypes/CustomTypes';

const contextStagesMap: Record<EtaContext, EtaStage[]> = {
  global: [
    { key: 'startingUp', label: t('loading.global.startingUp'), weight: 1 },
    { key: 'initializingAccount', label: t('loading.global.initializingAccount'), weight: 1.5 },
    { key: 'preparingMusicEngine', label: t('loading.global.preparingMusicEngine'), weight: 2 },
    { key: 'almostReady', label: t('loading.global.almostReady'), weight: 1 }
  ],
  refreshSamples: [
    { key: 'refreshSamples_start', label: t('loading.refreshSamples.refreshSamples_start'), weight: 1 },
    { key: 'refreshSamples_done', label: t('loading.refreshSamples.refreshSamples_done'), weight: 1 }
  ],
  loadXML: [
    { key: 'loadXML_start', label: t('loading.loadXML.loadXML_start'), weight: 2 },
    { key: 'loadXML_done', label: t('loading.loadXML.loadXML_done'), weight: 1 }
  ],
  uploadXML: [
    { key: 'uploadXML_start', label: t('loading.uploadXML.uploadXML_start'), weight: 1 },
    { key: 'uploadXML_done', label: t('loading.uploadXML.uploadXML_done'), weight: 1 }
  ],
  editXML: [
    { key: 'editXML_process', label: t('loading.editXML.editXML_process'), weight: 1 },
  ],
  deleteXML: [
    { key: 'deleteXML_process', label: t('loading.deleteXML.deleteXML_process'), weight: 1 },
  ],
  CSV: [
    { key: 'CSV_process', label: t('loading.CSV.CSV_process'), weight: 1 },
  ],
  importCSV: [
    { key: 'importCSV_start', label: t('loading.importCSV.importCSV_start'), weight: 1 },
    { key: 'importCSV_processing', label: t('loading.importCSV.importCSV_processing'), weight: 2 },
    { key: 'importCSV_done', label: t('loading.importCSV.importCSV_done'), weight: 1 },
  ],
  exportCSV: [
    { key: 'exportCSV_processingUrls', label: t('loading.exportCSV.exportCSV_processingUrls'), weight: 8 },
  ],
  quickEvent: [
    { key: 'quickEvent', label: t('loading.quickEvent.quickEvent'), weight: 1 },
  ]
};


class EtaLoader implements IEtaLoader {
  stages: EtaStage[] = [];
  currentStageIndex: number = 0;
  context: EtaContext | null = null;
  animatedPercent: number = 0;
  animationTimer: any = null;
  animationTarget: number = 0;
  stageStartTime: number = 0; // Track when stage started
  minStageDuration: number = 600; // Minimum time per stage in ms
  stageTimeoutUnexpectedDuration: number = 10000; // Unusual time per stage in ms
  stageTimeoutErrorDuration: number = 18000; // Max time per stage in ms
  stageTimeout: any = null;
  stageErrorTimeout: any = null;
  currentTimeoutLevel: 'none' | 'unexpected' | 'error' = 'none'; // Track which timeout has been triggered
  globalContextFinished: boolean = false;
  timeoutCheckInterval: any = null; // for periodic timeout checks
  timeoutCheckStartTime: number = 0; // Track when interval checking started
  maxTimeoutCheckDuration: number = 30000; // Maximum 30 seconds for timeout checking
  
  // Session tracking to prevent parallel executions from interfering
  activeSessionId: string | null = null;

  private _hasError: boolean = false; // Error state for error UI instead of loader
  
  get hasError() {
    return this._hasError;
  }
  
  set hasError(value: boolean) {
    this._hasError = value;
  }
  
  errorMessage: string = '';
  errorAction: (() => void) | null = null;

  constructor() {
    makeAutoObservable(this);
    this.animatedPercent = 0;
  }

  // Start checking timeouts periodically
  private startTimeoutChecking() {
    if (this.timeoutCheckInterval) {
      clearInterval(this.timeoutCheckInterval);
    }
    this.timeoutCheckStartTime = Date.now(); // Track when checking started
    
    this.timeoutCheckInterval = setInterval(() => {
      // Stop if we've been checking for too long (absolute limit)
      const totalElapsed = Date.now() - this.timeoutCheckStartTime;
      if (totalElapsed > this.maxTimeoutCheckDuration) {
        console.log('[DEBUG_ERROR] [ETA]⏹️ Timeout checking exceeded max duration (30s), stopping');
        this.stopTimeoutChecking();
        return;
      }
      
      this.checkStageTimeout();
    }, 1000); // Check every second
  }

  // Stop timeout checking
  private stopTimeoutChecking() {
    if (this.timeoutCheckInterval) {
      console.log('[DEBUG_ERROR] [ETA]🛑 Stopping timeout checking');
      clearInterval(this.timeoutCheckInterval);
      this.timeoutCheckInterval = null;
      this.currentTimeoutLevel = 'none';
    }
  }

  public checkStageTimeout() {
    if (!this.stageStartTime) {
      console.log('[DEBUG_ERROR] [ETA] stageStartTime not set yet');
      return;
    }

    // Skip timeout checks for fast operations like importCSV, CSV, quickEvent
    const quickContexts: EtaContext[] = ['importCSV', 'CSV', 'quickEvent', 'editXML', 'deleteXML'];
    if (this.context && quickContexts.includes(this.context)) {
      return;
    }

    const elapsed = Date.now() - this.stageStartTime;
    const current = this.stages[this.currentStageIndex];
    
    if (!current || current.progress === 1) return;

    // Stop checking after 25 seconds - nothing more we can do
    if (elapsed > 25000) {
      console.log('[DEBUG_ERROR] [ETA]⏹️ 25s exceeded, stopping timeout checks');
      this.stopTimeoutChecking();
      return;
    }

    if (elapsed > this.stageTimeoutErrorDuration && this.currentTimeoutLevel !== 'error') {
      this.currentTimeoutLevel = 'error';
      runInAction(() => {
        this.stages[this.currentStageIndex].label = t('loading.error.loading_error_retry');
      });
      console.log('[DEBUG_ERROR] 🔴 Error timeout fired (18s+), label set to:', this.stages[this.currentStageIndex].label);
    } else if (elapsed > this.stageTimeoutUnexpectedDuration && this.currentTimeoutLevel === 'none') {
      this.currentTimeoutLevel = 'unexpected';
      runInAction(() => {
        this.stages[this.currentStageIndex].label = t('loading.error.loading_error_timeout');
      });
      console.log('[DEBUG_ERROR] 🟡 Unexpected timeout fired (10s+), label set to:', this.stages[this.currentStageIndex].label);
    }
  }

  setContext(context: EtaContext, sessionId?: string) {
  // Validate session if provided
  if (sessionId && !this.isSessionActive(sessionId)) {
    console.log('[DEBUG_ERROR] [ETA]⛔ Session', sessionId, 'is no longer active. Ignoring setContext.');
    return;
  }
  // Enforce: first context must be 'global' and must finish before any other
  if (!this.globalContextFinished && context !== 'global') {
    console.log('[DEBUG_ERROR] [ETA]⛔ Cannot set context to', context, 'before global context is finished');
    return;
  }
  if (
    this.stages[this.stages.length - 1]?.progress !== 1 &&
    this.context !== null &&
    context !== this.context
  ) {
    console.log('[DEBUG_ERROR] [ETA]⚠️ Attempted to set context before finishing last stage');
    return;
  }
  console.log('[DEBUG_ERROR] [ETA]🔄 Setting context:', context, 'for session:', sessionId);
  this.context = context;
  this.stageStartTime = 0; // Reset stage start time for new context
  // Automatically set stages for this context
  const stages = contextStagesMap[context];
  if (stages) {
    this.setStages(stages, context);
    // Optionally, set first stage to 0 progress for instant spinner
    if (stages.length > 0) {
      this.setStage(stages[0].key, 0, undefined, sessionId);
    }
  }
}

  // Clears the current context (call after last stage of current context is finished)
  clearContext() {
  if(this.stages[this.stages.length - 1]?.progress !== 1) {
    console.log('[DEBUG_ERROR] [ETA]⚠️ Attempted to clear context before finishing last stage');
    return;
  } else {
    console.log('[DEBUG_ERROR] [ETA]🧹 Clearing context:', this.context);
    this.stopTimeoutChecking();
    this.context = null;
    this.stages = [];
    this.currentStageIndex = 0;
    this.animatedPercent = 0;
    this.stageStartTime = 0; // Reset stage start time
    // Don't clear error state here - it should persist until explicitly cleared
  }
}

  // Show error state in the loader
  showError(message: string, action?: () => void) {
    this.hasError = true;
    this.errorMessage = message;
    this.errorAction = action || null;
    // Don't stop timeout checking - let it continue to show timeout progression
    // this.stopTimeoutChecking();
  }

  // Clear error state
  clearError() {
    this.hasError = false;
    this.errorMessage = '';
    this.errorAction = null;
  }

  // Start a new loading session and return its ID
  // This cancels any previous session
  startSession(): string {
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.activeSessionId = sessionId;
    console.log('[DEBUG_ERROR] [ETA] 🆕 Started new session:', sessionId);
    return sessionId;
  }

  // Check if a session is still active
  isSessionActive(sessionId: string): boolean {
    return this.activeSessionId === sessionId;
  }

  // Placeholder: set stages for a context
  setStages(stages: EtaStage[], context: EtaContext) {
    if (
      this.context === 'global' &&
      this.stages?.[this.stages.length - 1]?.progress !== 1 &&
      context !== 'global'
    ) {
      console.log('[DEBUG_ERROR] [ETA]⛔ Blocked context switch: global context not finished');
      return;
    }
    this.stages = stages.map(s => ({ ...s, progress: 0 }));
    this.currentStageIndex = 0;
    this.context = context;
    this.animatedPercent = 0;
    this.stageStartTime = 0; // Reset stage start time for new stages
    console.log(`[DEBUG_ERROR] [ETA]✅ Context switched to '${context}'`);
  }

  // Smoothly animate percent to target using requestAnimationFrame
  animateToPercent(targetPercent: number, duration = 200) {
    if (this.animationTimer) cancelAnimationFrame(this.animationTimer);
    const start = this.animatedPercent;
    const end = targetPercent;
    const startTime = performance.now();
    this.animationTarget = end;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      this.animatedPercent = start + (end - start) * progress;
      if (progress < 1) {
        this.animationTimer = requestAnimationFrame(animate);
      } else {
        this.animatedPercent = end && end > 100 ? 100 : end;
        this.animationTimer = null;
      }
    };

    this.animationTimer = requestAnimationFrame(animate);
  }

  // Placeholder: set current stage by key
  setStage(key: string, progress = 0, label?: string, sessionId?: string) {
  // Validate session if provided
  if (sessionId && !this.isSessionActive(sessionId)) {
    console.log('[DEBUG_ERROR] [ETA]⛔ Session', sessionId, 'is no longer active. Ignoring setStage', key);
    return;
  }
  const idx = this.stages.findIndex(s => s.key === key);
  const isFirstStage = idx === 0;
  const prevStageComplete = isFirstStage || this.stages[idx - 1]?.progress === 1;

  if (
    idx !== -1 && progress >= 0 &&
    progress <= 1 &&
    progress >= (this.stages[idx].progress ?? 0) &&
    progress !== this.stages[idx].progress &&
    prevStageComplete
  ) {
    if (idx !== this.currentStageIndex) {
      this.stageStartTime = Date.now();
      this.currentTimeoutLevel = 'none'; // Reset timeout level for new stage
      console.log(`[DEBUG_ERROR] [ETA] Starting stage ${key} at time:`, this.stageStartTime);
      // Start timeout checking only when we actually start a stage
      if (!this.timeoutCheckInterval) {
        this.startTimeoutChecking();
      }
    } else if (!this.stageStartTime) {
      // If we're updating the current stage but stageStartTime was never set (e.g., first stage), set it now
      this.stageStartTime = Date.now();
      console.log(`[DEBUG_ERROR] [ETA] Starting stage ${key} (current stage, late init) at time:`, this.stageStartTime);
      if (!this.timeoutCheckInterval) {
        this.startTimeoutChecking();
      }
    }

    this.currentStageIndex = idx;
    this.stages[idx].progress = progress;

    // Check timeout after updating progress
    // this.checkStageTimeout();

    if (label) {
      this.stages[idx].label = label;
    }
    
    this.animateToPercent(this.percent);
    
    if (idx === this.stages.length - 1 && progress === 1) {
      if (this.context === 'global') {
        this.globalContextFinished = true;
        console.log('[DEBUG_ERROR] [ETA]✅ Global context finished (marked immediately)');
      }
      setTimeout(() => {
        this.clearContext();
      }, 2000);
    }
  }
}

  // Computed: get current percent (weighted)
  get percent() {
    const totalWeight = this.stages.reduce((sum, s) => sum + s.weight, 0) || 1;
    let doneWeight = 0;
    for (let i = 0; i < this.currentStageIndex; i++) {
      doneWeight += this.stages[i].weight;
    }
    const current = this.stages[this.currentStageIndex];
    const currentProgress = (current?.progress || 0) * (current?.weight || 0);
    return Math.round(((doneWeight + currentProgress) / totalWeight) * 100);
  }

  // Use this for smooth progress in UI
  get animatedPercentValue() {
    return Math.round(this.animatedPercent);
  }

  // Computed: get current message
  get message() {
    return this.stages[this.currentStageIndex]?.label || '';
  }
}

// Create and attach singleton to window
const etaLoader = new EtaLoader();
if (typeof window !== 'undefined') {
  (window as any).etaLoader = etaLoader;
}

export default etaLoader;